import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/cn';

const ITEMS = [
  { to: '/', label: 'Dashboard', icon: HomeIcon, end: true },
  { to: '/shows', label: 'Shows', icon: ShowsIcon, end: false },
  { to: '/practice', label: 'Practice', icon: PracticeIcon, end: false },
  { to: '/progress', label: 'Progress', icon: ProgressIcon, end: false },
  { to: '/donate', label: 'Donate', icon: DonateIcon, end: false },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, end: false },
] as const;

export function BottomNav() {
  return (
    <nav
      aria-label="Primary"
      className={cn(
        'bg-white/95 dark:bg-ink-950/95 border-ink-200 dark:border-ink-800',
        'safe-bottom fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur',
      )}
    >
      <ul className="mx-auto flex max-w-2xl">
        {ITEMS.map(({ to, label, icon: Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex min-h-14 flex-col items-center justify-center gap-1 py-2 text-[0.6875rem] font-medium',
                  isActive ? 'text-brand-600 dark:text-brand-400' : 'text-ink-500 dark:text-ink-400',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon active={isActive} />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

interface IconProps {
  active: boolean;
}

function iconClass(active: boolean) {
  return cn('size-6', active ? 'stroke-[2.25]' : 'stroke-[1.75]');
}

function HomeIcon({ active }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" className={iconClass(active)}>
      <path d="M3 10.5 12 3l9 7.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 9.5V21h14V9.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShowsIcon({ active }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" className={iconClass(active)}>
      <path d="M4 4h7v16H4z" strokeLinejoin="round" />
      <path d="M13 4h7v16h-7z" strokeLinejoin="round" />
    </svg>
  );
}

function PracticeIcon({ active }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" className={iconClass(active)}>
      <path d="M12 3v11" strokeLinecap="round" />
      <path d="M9 6a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z" strokeLinejoin="round" />
      <path d="M6 11a6 6 0 0 0 12 0M9 21h6" strokeLinecap="round" />
    </svg>
  );
}

function ProgressIcon({ active }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" className={iconClass(active)}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" strokeLinecap="round" />
    </svg>
  );
}

function DonateIcon({ active }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" className={iconClass(active)}>
      <path
        d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SettingsIcon({ active }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" className={iconClass(active)}>
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.88 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 2.6 15H2.5a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 7L4.54 7a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6h.1a2 2 0 1 1 4 0V4.6A1.7 1.7 0 0 0 17 4.6l.06-.06a2 2 0 1 1 2.83 2.83L19.83 7A1.7 1.7 0 0 0 19.4 9v.1a2 2 0 1 1 0 4z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
